import React from 'react';
import { Badge } from '@/shared/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { AssignmentList } from '@/modules/deals/components/assignments';
import { MY_ASSIGNMENT_TABS } from '@/modules/deals/utils';
import { InProgress, Pending, Award, Send } from '../../../../shared/assets/AppIcons';

export default function AssignmentsTabs({
  activeTab,
  onTabChange,
  incomingAccepted,
  incomingPending,
  completedAssignments,
  myRequests = [],
  isClientFiltered = false,
  clientFilterLabel = '',
  currentUser,
  staff = [],
  clients = [],
  clientsCatalogStatus = 'empty',
  expandedAssignment,
  commentDrafts,
  assignmentActionStates = {},
  canDelete = false,
  onToggleExpand,
  onDelete,
  onAccept,
  onReject,
  onComplete,
  onUpdateClient,
  onEdit,
  onCommentDraftChange,
  onPublishComment,
  onOpenCreate,
  onOpenDetail,
}) {
  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="assignments-tabs">
      {isClientFiltered ? (
        <div className="assignments-tabs__client-filter">
          Filtro cliente attivo: {clientFilterLabel || 'Cliente selezionato'}
        </div>
      ) : null}

      <TabsList className="assignments-tabs__list">
        {MY_ASSIGNMENT_TABS.map((tab) => {
          const Icon = tab.icon;
          const countMap = {
            mie_richieste: myRequests.length,
            in_corso: incomingAccepted.length,
            da_accettare: incomingPending.length,
            completati: completedAssignments.length,
          };

          const count = countMap[tab.value];

          return (
            <TabsTrigger key={tab.value} value={tab.value} className="assignments-tabs__trigger">
              <Icon size={16} />
              <span>{tab.label}</span>
              {count > 0 ? (
                <Badge
                  variant={tab.value === 'da_accettare' ? 'default' : 'secondary'}
                  className={tab.value === 'da_accettare' ? 'ml-1 bg-[var(--warning)] text-[var(--on-primary)]' : 'ml-1'}
                >
                  {count}
                </Badge>
              ) : null}
            </TabsTrigger>
          );
        })}
      </TabsList>

      <TabsContent value="mie_richieste">
        <div className="assignments-tabs__intro">
          <p>
            {isClientFiltered
              ? 'Task che hai richiesto collegati al cliente'
              : 'Task inviati al team in attesa di accettazione'}
          </p>
        </div>

        <AssignmentList
          assignments={myRequests}
          currentUser={currentUser}
          staff={staff}
          clients={clients}
          clientsCatalogStatus={clientsCatalogStatus}
          expandedAssignment={expandedAssignment}
          commentDrafts={commentDrafts}
          assignmentActionStates={assignmentActionStates}
          canDelete={canDelete}
          canReject={false}
          emptyState={{
            icon: Send,
            title: 'Nessuna richiesta in attesa',
            description: 'Le richieste accettate sono in In corso; quelle concluse in Completati.',
            showButton: false,
          }}
          onToggleExpand={onToggleExpand}
          onDelete={onDelete}
          onAccept={onAccept}
          onReject={onReject}
          onComplete={onComplete}
          onUpdateClient={onUpdateClient}
          onEdit={onEdit}
          onCommentDraftChange={onCommentDraftChange}
          onPublishComment={onPublishComment}
          onCreate={onOpenCreate}
          onOpenDetail={onOpenDetail}
        />
      </TabsContent>

      <TabsContent value="in_corso">
        <div className="assignments-tabs__intro">
          <p>
            {isClientFiltered
              ? 'Task in corso collegati al cliente'
              : 'Task che ti sono stati assegnati e che hai accettato'}
          </p>
        </div>

        <AssignmentList
          assignments={incomingAccepted}
          currentUser={currentUser}
          staff={staff}
          clients={clients}
          clientsCatalogStatus={clientsCatalogStatus}
          expandedAssignment={expandedAssignment}
          commentDrafts={commentDrafts}
          assignmentActionStates={assignmentActionStates}
          showSender={true}
          canComplete={true}
          canReject={false}
          emptyState={{
            icon: InProgress,
            title: 'Nessun task in corso',
            description: 'Non hai task attivi assegnati da altri',
            showButton: false,
          }}
          onToggleExpand={onToggleExpand}
          onDelete={onDelete}
          onAccept={onAccept}
          onReject={onReject}
          onComplete={onComplete}
          onUpdateClient={onUpdateClient}
          onEdit={onEdit}
          onCommentDraftChange={onCommentDraftChange}
          onPublishComment={onPublishComment}
          onCreate={onOpenCreate}
          onOpenDetail={onOpenDetail}
        />
      </TabsContent>

      <TabsContent value="da_accettare">
        <div className="assignments-tabs__intro">
          <p>
            {isClientFiltered
              ? 'Task da accettare collegati al cliente'
              : 'Task che ti sono stati assegnati e attendono la tua risposta'}
          </p>
        </div>

        <AssignmentList
          assignments={incomingPending}
          currentUser={currentUser}
          staff={staff}
          clients={clients}
          clientsCatalogStatus={clientsCatalogStatus}
          expandedAssignment={expandedAssignment}
          commentDrafts={commentDrafts}
          assignmentActionStates={assignmentActionStates}
          showSender={true}
          showActions={true}
          canReject={true}
          emptyState={{
            icon: Pending,
            title: 'Nessun task in attesa',
            description: 'Non hai task da accettare al momento',
            showButton: false,
          }}
          onToggleExpand={onToggleExpand}
          onDelete={onDelete}
          onAccept={onAccept}
          onReject={onReject}
          onComplete={onComplete}
          onUpdateClient={onUpdateClient}
          onEdit={onEdit}
          onCommentDraftChange={onCommentDraftChange}
          onPublishComment={onPublishComment}
          onCreate={onOpenCreate}
          onOpenDetail={onOpenDetail}
        />
      </TabsContent>

      <TabsContent value="completati">
        <div className="assignments-tabs__intro">
          <p>Storico dei task completati</p>
        </div>

        <AssignmentList
          assignments={completedAssignments}
          currentUser={currentUser}
          staff={staff}
          clients={clients}
          clientsCatalogStatus={clientsCatalogStatus}
          expandedAssignment={expandedAssignment}
          commentDrafts={commentDrafts}
          assignmentActionStates={assignmentActionStates}
          showSender={false}
          canReject={false}
          emptyState={{
            icon: Award,
            title: 'Nessun task completato',
            description: 'I task completati appariranno qui',
            showButton: false,
          }}
          onToggleExpand={onToggleExpand}
          onDelete={onDelete}
          onAccept={onAccept}
          onReject={onReject}
          onComplete={onComplete}
          onUpdateClient={onUpdateClient}
          onEdit={onEdit}
          onCommentDraftChange={onCommentDraftChange}
          onPublishComment={onPublishComment}
          onCreate={onOpenCreate}
          onOpenDetail={onOpenDetail}
        />
      </TabsContent>
    </Tabs>
  );
}
